import { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = '/api';

async function publicRequest(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export default function PublicSigningPage() {
  const { token } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [printName, setPrintName] = useState('');
  const [title, setTitle] = useState('Community Manager');
  const custCanvasRef = useRef(null);
  const [hasSigned, setHasSigned] = useState(false);

  useEffect(() => {
    publicRequest(`/public/agreements/${token}`)
      .then(data => { setAgreement(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [token]);

  function formatDateDisplay(dateStr) {
    if (!dateStr) return '_____ day of ____________, 20____';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const day = d.getDate();
      const s = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
      return `${day}${s} day of ${d.toLocaleDateString('en-US', { month: 'long' })}, ${d.getFullYear()}`;
    } catch { return dateStr; }
  }

  function formatSimpleDate(dateStr) {
    if (!dateStr) return '________________';
    try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return dateStr; }
  }

  async function handleSign() {
    if (!hasSigned) return setError('Please sign above');
    if (!printName.trim()) return setError('Please print your name');
    setError('');
    setSubmitting(true);

    try {
      const sig = custCanvasRef.current.toDataURL('image/png');
      await publicRequest(`/public/agreements/${token}/sign`, {
        method: 'POST',
        body: JSON.stringify({
          customer_signature: sig,
          customer_print_name: printName,
          customer_title: title,
        }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to sign');
    }
    setSubmitting(false);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>Loading agreement...</div>
        </div>
      </div>
    );
  }

  if (error && !agreement) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128683;</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Agreement Not Found</div>
          <div style={{ fontSize: 14, color: '#64748b' }}>{error}</div>
        </div>
      </div>
    );
  }

  if (agreement?.already_signed || success) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#9989;</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
            {success ? 'Agreement Signed!' : 'Already Signed'}
          </div>
          <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
            {success
              ? 'Thank you for signing the service agreement. Apollo Towing will be in touch.'
              : 'This agreement has already been signed. If you need a copy, please contact Apollo Towing.'}
          </div>
          <div style={{ marginTop: 24 }}>
            <img src="/logo-agreement.png" alt="Apollo Towing" style={{ width: 200, opacity: 0.6 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px 16px' }}>
      <div style={{
        background: '#fff', border: '1px solid #d1d5db', padding: '60px 72px',
        maxWidth: 800, margin: '0 auto',
        fontFamily: "'Times New Roman', Times, serif", fontSize: 14, lineHeight: 1.7,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo-agreement.png" alt="Apollo Towing" style={{ width: 340, maxWidth: '80%' }} />
        </div>

        <h1 style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, margin: '0 0 28px', fontFamily: "'Times New Roman', serif" }}>
          SERVICE AGREEMENT
        </h1>

        <p style={{ margin: '0 0 14px' }}>
          This General Service Agreement (the &ldquo;Agreement&rdquo;) dated this{' '}
          <strong style={{ textDecoration: 'underline' }}>{formatDateDisplay(agreement.agreement_date)}</strong>
          {' '}is between{' '}
          <strong style={{ textDecoration: 'underline' }}>{agreement.customer_name}</strong>
          , herein referred to as &ldquo;Customer&rdquo; and{' '}
          <strong style={{ textDecoration: 'underline' }}>Apollo Towing LLC</strong>
          , herein referred to as &ldquo;Service Provider&rdquo;.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          Service Provider has agreed to provide services to the Customer on the terms and conditions set out in this Agreement, while Customer is of the opinion that Service Provider has the proper and necessary qualifications, experience and abilities to provide services to Customer.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          Therefore in consideration of the matters described above, the receipt and sufficiency of which consideration is hereby acknowledged, the Customer and the Service Provider agree as follows:
        </p>

        <p style={{ margin: '0 0 6px' }}>
          <strong>1. Scope of Work</strong>: The Service Provider is to provide the Customer with the following services (the &ldquo;Services&rdquo;):
        </p>
        <ul style={{ paddingLeft: 40, margin: '0 0 14px' }}>
          <li>Property patrols for removal of any vehicles not following property parking rules.</li>
          <li>Towing is only authorized upon written request by the property manager on duty.</li>
          <li>The services will include any other tasks which the Customer and the Service Provider may agree on.</li>
        </ul>

        <p style={{ margin: '0 0 14px' }}>
          <strong>2. Term of Agreement</strong>: This Agreement will begin on{' '}
          <strong style={{ textDecoration: 'underline' }}>{formatSimpleDate(agreement.start_date)}</strong>
          {' '}and will remain in full force and effect until either party seeks termination.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          <strong>3. Termination</strong>: If either party seeks termination of this Agreement, the terminating party must provide a 30 day written notice to the other party.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          <strong>4. Independent Contractor</strong>: The Service Provider is acting as an independent contractor in providing the Services under this Agreement, not as an employee. The parties agree that this Agreement does not create a joint venture or a partnership between them.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          <strong>5. Property Address:</strong>{' '}
          <span style={{ textDecoration: 'underline' }}>{agreement.property_address}</span>
        </p>

        {/* Signature Section */}
        <div style={{ marginTop: 40, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>

          {/* Left — Customer signs here */}
          <div>
            <SignaturePad ref={custCanvasRef} label="Sign Here" onSign={() => setHasSigned(true)} onClear={() => setHasSigned(false)} />

            {error && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 6 }}>{error}</div>
            )}

            <div style={{ marginTop: 12, fontFamily: 'Arial, sans-serif' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Print Name *</label>
              <input type="text" value={printName} onChange={e => setPrintName(e.target.value)} placeholder="Your full name" className="sales-input" />
            </div>
            <div style={{ marginTop: 8, fontFamily: 'Arial, sans-serif' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="sales-input" />
            </div>
          </div>

          {/* Right — Apollo's signature (already signed) */}
          <div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
              Apollo Towing Authorized Representative
            </div>
            <div style={{
              width: '100%', height: 80, border: '1px solid #e2e8f0', borderRadius: 8,
              background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>Signed by {agreement.rep_print_name}</span>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{agreement.rep_print_name}</div>
              <div style={{ fontSize: 12, color: '#64748b', borderTop: '1px solid #d1d5db', paddingTop: 4, marginTop: 4 }}>Print</div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>Regional Property Manager</div>
          </div>
        </div>
      </div>

      {/* Sign Button */}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <button onClick={handleSign} disabled={submitting}
          style={{
            padding: '14px 48px', fontSize: 16, fontWeight: 700, borderRadius: 10,
            background: '#0f172a', color: '#fff', border: 'none', cursor: 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}>
          {submitting ? 'Signing...' : 'Sign Agreement'}
        </button>
      </div>

      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#94a3b8' }}>
        Apollo Towing LLC &bull; Changing the Towing Industry
      </div>
    </div>
  );
}

// ── Signature Pad ─────────────────────────────────────────────
const SignaturePad = forwardRef(function SignaturePad({ label, onSign, onClear }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => { if (ref) ref.current = canvasRef.current; }, [ref]);

  const getPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width), y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  }, []);

  const start = useCallback((e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }, [getPos]);
  const move = useCallback((e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#000'; ctx.lineTo(p.x, p.y); ctx.stroke(); if (!hasDrawn.current) { hasDrawn.current = true; onSign?.(); } }, [getPos, onSign]);
  const stop = useCallback(() => { drawing.current = false; }, []);
  function clear() { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); hasDrawn.current = false; onClear?.(); }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontFamily: 'Arial, sans-serif' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{label} *</span>
        <button type="button" onClick={clear} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear</button>
      </div>
      <canvas ref={canvasRef} width={400} height={120}
        onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
        style={{ width: '100%', height: 80, border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'crosshair', touchAction: 'none' }} />
    </div>
  );
});
